import { useMemo, useRef, useState } from "react";
import {
  ArrowDownToLine,
  Braces,
  FileText,
  RefreshCcw,
  ScissorsLineDashed,
  Upload
} from "lucide-react";

const defaultOptions = {
  mode: "line",
  separatorType: "spaces",
  spaceCount: 4,
  customSeparator: "    ",
  lengthPolicy: "longest",
  missingPolicy: "single",
  emptyReplacement: "",
  linePrefix: "",
  lineEnding: "lf",
  outputName: "merged.txt"
};

function splitLines(text) {
  if (!text) return [];
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  if (text.endsWith("\n") || text.endsWith("\r")) {
    lines.pop();
  }
  return lines;
}

function getLineEnding(type) {
  return type === "crlf" ? "\r\n" : "\n";
}

function getSeparator(options) {
  if (options.separatorType === "custom") return options.customSeparator;
  return " ".repeat(Number(options.spaceCount) || 0);
}

function normalizeOutputName(name) {
  const cleanName = name.trim() || "merged.txt";
  return cleanName.toLowerCase().endsWith(".txt") ? cleanName : `${cleanName}.txt`;
}

function pickLength(aLines, bLines, policy) {
  if (policy === "shortest") return Math.min(aLines.length, bLines.length);
  if (policy === "first") return aLines.length;
  if (policy === "second") return bLines.length;
  return Math.max(aLines.length, bLines.length);
}

function mergeLinePair(left, right, separator, options) {
  const leftExists = left !== undefined;
  const rightExists = right !== undefined;
  const safeLeft = leftExists ? left : options.emptyReplacement;
  const safeRight = rightExists ? right : options.emptyReplacement;

  if (options.missingPolicy === "placeholder") {
    return `${safeLeft}${separator}${safeRight}`;
  }

  if (options.missingPolicy === "keep-separator") {
    return `${leftExists ? left : ""}${separator}${rightExists ? right : ""}`;
  }

  if (!leftExists) return rightExists ? right : "";
  if (!rightExists) return left;
  return `${left}${separator}${right}`;
}

function applyLinePrefix(lines, prefix) {
  if (!prefix) return lines;
  return lines.map((line) => `${prefix}${line}`);
}

function buildMergedText(fileA, fileB, options) {
  const aLines = splitLines(fileA.text);
  const bLines = splitLines(fileB.text);
  const separator = getSeparator(options);
  const lineEnding = getLineEnding(options.lineEnding);
  let mergedLines = [];

  if (options.mode === "append") {
    mergedLines = [...aLines, ...bLines];
  } else if (options.mode === "interleave") {
    const count = pickLength(aLines, bLines, options.lengthPolicy);

    for (let index = 0; index < count; index += 1) {
      if (aLines[index] !== undefined) mergedLines.push(aLines[index]);
      if (bLines[index] !== undefined) mergedLines.push(bLines[index]);
    }
  } else {
    const count = pickLength(aLines, bLines, options.lengthPolicy);
    mergedLines = Array.from({ length: count }, (_, index) =>
      mergeLinePair(aLines[index], bLines[index], separator, options)
    );
  }

  return applyLinePrefix(mergedLines, options.linePrefix).join(lineEnding);
}

function countNonEmpty(lines) {
  return lines.filter((line) => line.length > 0).length;
}

function FileDrop({ label, file, onFile }) {
  const inputRef = useRef(null);

  async function handleFile(nextFile) {
    if (!nextFile) return;
    const text = await nextFile.text();
    onFile({
      name: nextFile.name,
      size: nextFile.size,
      text
    });
  }

  return (
    <section
      className="file-drop"
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        handleFile(event.dataTransfer.files?.[0]);
      }}
    >
      <div className="file-drop__icon" aria-hidden="true">
        <Upload size={22} />
      </div>
      <div>
        <h2>{label}</h2>
        <p>{file.name ? file.name : "拖入 txt 文件，或点击选择"}</p>
      </div>
      <button className="ghost-button" type="button" onClick={() => inputRef.current?.click()}>
        <Upload size={16} />
        选择文件
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".txt,text/plain"
        onChange={(event) => handleFile(event.target.files?.[0])}
      />
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="stat">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export default function App() {
  const [fileA, setFileA] = useState({ name: "", size: 0, text: "" });
  const [fileB, setFileB] = useState({ name: "", size: 0, text: "" });
  const [options, setOptions] = useState(defaultOptions);

  const mergedText = useMemo(() => buildMergedText(fileA, fileB, options), [fileA, fileB, options]);
  const linesA = splitLines(fileA.text);
  const linesB = splitLines(fileB.text);
  const outputLines = splitLines(mergedText);
  const previewLines = outputLines.slice(0, 80);
  const canExport = fileA.text.length > 0 || fileB.text.length > 0;

  function updateOption(key, value) {
    setOptions((current) => ({ ...current, [key]: value }));
  }

  function downloadMergedFile() {
    if (!canExport) return;
    const blob = new Blob([mergedText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = normalizeOutputName(options.outputName);
    link.click();
    URL.revokeObjectURL(url);
  }

  function resetAll() {
    setFileA({ name: "", size: 0, text: "" });
    setFileB({ name: "", size: 0, text: "" });
    setOptions(defaultOptions);
  }

  return (
    <main className="app-shell">
      <section className="topbar">
        <div className="brand">
          <span className="brand__mark">
            <ScissorsLineDashed size={22} />
          </span>
          <div>
            <h1>TXT 合并工具</h1>
            <p>上传两个文本文件，自定义合并规则后导出新的 txt。</p>
          </div>
        </div>
        <div className="topbar__actions">
          <button className="ghost-button" type="button" onClick={resetAll}>
            <RefreshCcw size={16} />
            重置
          </button>
          <button className="primary-button" type="button" disabled={!canExport} onClick={downloadMergedFile}>
            <ArrowDownToLine size={17} />
            导出 txt
          </button>
        </div>
      </section>

      <section className="workspace">
        <div className="left-rail">
          <FileDrop label="文件 A" file={fileA} onFile={setFileA} />
          <FileDrop label="文件 B" file={fileB} onFile={setFileB} />

          <section className="panel">
            <div className="panel__title">
              <Braces size={18} />
              <h2>合并规则</h2>
            </div>

            <label className="field">
              <span>合并方式</span>
              <select value={options.mode} onChange={(event) => updateOption("mode", event.target.value)}>
                <option value="line">相同行合并</option>
                <option value="append">文件 B 追加到文件 A</option>
                <option value="interleave">A/B 行交错输出</option>
              </select>
            </label>

            <label className="field">
              <span>行数范围</span>
              <select
                value={options.lengthPolicy}
                onChange={(event) => updateOption("lengthPolicy", event.target.value)}
                disabled={options.mode === "append"}
              >
                <option value="longest">保留较长文件的全部行</option>
                <option value="shortest">只保留两个文件都有的行</option>
                <option value="first">以文件 A 行数为准</option>
                <option value="second">以文件 B 行数为准</option>
              </select>
            </label>

            <fieldset className="segmented" disabled={options.mode !== "line"}>
              <legend>分隔符</legend>
              <label>
                <input
                  type="radio"
                  name="separator"
                  checked={options.separatorType === "spaces"}
                  onChange={() => updateOption("separatorType", "spaces")}
                />
                空格
              </label>
              <label>
                <input
                  type="radio"
                  name="separator"
                  checked={options.separatorType === "custom"}
                  onChange={() => updateOption("separatorType", "custom")}
                />
                自定义
              </label>
            </fieldset>

            {options.separatorType === "spaces" ? (
              <label className="field" aria-disabled={options.mode !== "line"}>
                <span>空格数量：{options.spaceCount}</span>
                <input
                  type="range"
                  min="0"
                  max="24"
                  value={options.spaceCount}
                  disabled={options.mode !== "line"}
                  onChange={(event) => updateOption("spaceCount", event.target.value)}
                />
              </label>
            ) : (
              <label className="field" aria-disabled={options.mode !== "line"}>
                <span>自定义分隔内容</span>
                <input
                  type="text"
                  value={options.customSeparator}
                  disabled={options.mode !== "line"}
                  onChange={(event) => updateOption("customSeparator", event.target.value)}
                  placeholder="例如： | "
                />
              </label>
            )}

            <label className="field">
              <span>缺失行处理</span>
              <select
                value={options.missingPolicy}
                onChange={(event) => updateOption("missingPolicy", event.target.value)}
                disabled={options.mode !== "line"}
              >
                <option value="single">只输出已有内容</option>
                <option value="keep-separator">保留分隔符</option>
                <option value="placeholder">使用占位内容</option>
              </select>
            </label>

            <label className="field">
              <span>缺失行占位</span>
              <input
                type="text"
                value={options.emptyReplacement}
                disabled={options.mode !== "line" || options.missingPolicy !== "placeholder"}
                onChange={(event) => updateOption("emptyReplacement", event.target.value)}
                placeholder="可留空"
              />
            </label>

            <label className="field">
              <span>每行开头增加内容</span>
              <input
                type="text"
                value={options.linePrefix}
                onChange={(event) => updateOption("linePrefix", event.target.value)}
                placeholder="例如：# 、序号前缀、缩进"
              />
            </label>

            <label className="field">
              <span>换行格式</span>
              <select value={options.lineEnding} onChange={(event) => updateOption("lineEnding", event.target.value)}>
                <option value="lf">LF（Unix/macOS）</option>
                <option value="crlf">CRLF（Windows）</option>
              </select>
            </label>

            <label className="field">
              <span>导出文件名</span>
              <input
                type="text"
                value={options.outputName}
                onChange={(event) => updateOption("outputName", event.target.value)}
              />
            </label>
          </section>
        </div>

        <section className="preview-panel">
          <div className="preview-panel__header">
            <div>
              <div className="panel__title">
                <FileText size={18} />
                <h2>合并预览</h2>
              </div>
              <p>预览前 80 行，导出会包含完整内容。</p>
            </div>
            <div className="stats">
              <Stat label="A 行数" value={linesA.length} />
              <Stat label="B 行数" value={linesB.length} />
              <Stat label="输出行数" value={outputLines.length} />
              <Stat label="非空行" value={countNonEmpty(outputLines)} />
            </div>
          </div>

          <div className="preview-box" aria-live="polite">
            {canExport ? (
              <ol>
                {previewLines.map((line, index) => (
                  <li key={`${index}-${line}`}>
                    <code>{line || " "}</code>
                  </li>
                ))}
              </ol>
            ) : (
              <div className="empty-state">
                <FileText size={42} />
                <h2>等待两个 txt 文件</h2>
                <p>先选择文件 A 和文件 B，然后在左侧调整合并方式。</p>
              </div>
            )}
          </div>
        </section>
      </section>
    </main>
  );
}
